/**
 * StorySessionPage组件 - 互动故事会话页面
 * 功能：
 * 1. 播放当前轮次的视频
 * 2. 展示分支选项供用户选择
 * 3. 处理继续故事（选择分支后生成新视频）
 * 4. 轮询会话状态（视频生成过程中）
 * 5. 显示故事历史
 * 6. 错误处理和重试机制
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storyAPI } from '../services/storyAPI';
import { addTouchOptimization, getDeviceType } from '../utils/responsive';

const StorySessionPage = () => {
  const { libraryId } = useParams(); // 统一使用 libraryId
  const navigate = useNavigate();
  
  // 当前实际的故事 ID（如果是 published 故事开始新交互，可能会更新为新的 draft 故事 ID）
  const [currentLibraryId, setCurrentLibraryId] = useState(libraryId ? parseInt(libraryId) : null);

  // 核心状态
  const [storyData, setStoryData] = useState(null); // 完整故事信息（从 getStoryLibraryItem 获取）
  // eslint-disable-next-line no-unused-vars
  const [currentRound, setCurrentRound] = useState(null); // 当前轮次数据（保留用于未来扩展）
  const [branches, setBranches] = useState([]); // 当前分支选项
  const [loading, setLoading] = useState(true); // 初始加载
  const [error, setError] = useState(null); // 错误状态

  // 视频生成状态
  // eslint-disable-next-line no-unused-vars
  const [status, setStatus] = useState('ready'); // generating | ready | error（保留用于未来扩展）
  const [isGenerating, setIsGenerating] = useState(false); // 是否正在生成视频
  // eslint-disable-next-line no-unused-vars
  const [generationError, setGenerationError] = useState(null); // 生成错误（保留用于未来扩展）
  const [selectedBranchId, setSelectedBranchId] = useState(null); // 当前选择的分支ID

  // 视频播放状态
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [currentSceneImageUrl, setCurrentSceneImageUrl] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [videoPrompt, setVideoPrompt] = useState(null); // 当前视频的提示词（保留用于未来扩展）
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  
  // 视频加载状态（用于过渡效果）
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  
  // 预加载视频引用
  const preloadVideoRef = useRef(null);
  
  // 音频解锁状态（用于自动播放声音）
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  
  // 视频加载错误状态（用于错误提示和重试）
  const [videoLoadError, setVideoLoadError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isVideoPlaying, setIsVideoPlaying] = useState(false); // 视频播放状态（保留用于未来扩展）
  // eslint-disable-next-line no-unused-vars
  const [videoEnded, setVideoEnded] = useState(false); // 视频结束状态（保留用于未来扩展）
  const [showVideoNav, setShowVideoNav] = useState(false); // 鼠标悬停展示上下轮导航

  // 故事历史
  const [storyHistory, setStoryHistory] = useState([]); // 已完成的轮次列表
  const [isMusicOn, setIsMusicOn] = useState(false); // BGM默认关闭
  const [bgmUrl, setBgmUrl] = useState(null);

  // 历史预览：当不为 null 时，预览指定 step（不允许选择分支）
  const [previewStepIndex, setPreviewStepIndex] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null); // 生成完成提示
  
  // 当前播放的视频索引（从0开始：0=故事背景，1=第一集，2=第二集...）
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(0);

  // 轮询相关
  // const pollingIntervalRef = useRef(null);
  // 右侧聊天容器引用，用于自动滚动到底部
  const rightPaneRef = useRef(null);
  const isRequestingRef = useRef(false);

  const isPreviewing = previewStepIndex !== null && storyHistory.length > 0;
  const isMobileDevice = getDeviceType() === 'mobile';
  // 预览步已选择的分支（用于显示标记与禁用）- 保留用于未来扩展
  // eslint-disable-next-line no-unused-vars
  const previewSelectedId = (() => {
    if (!isPreviewing) return null;
    const idx = Math.max(0, Math.min(previewStepIndex, storyHistory.length - 1));
    const step = storyHistory[idx];
    if (!step || !step.selected_branch) return null;
    const sb = step.selected_branch;
    // 统一为字符串比较
    return (sb.id || sb.branch_id || null) ? String(sb.id || sb.branch_id) : null;
  })();

  const buildRetryUrl = useCallback((url) => {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url, window.location.origin);
      parsedUrl.searchParams.set('_t', String(Date.now()));

      // 保持相对路径输入仍以相对路径形式返回，避免无意义地写回 origin。
      if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      }

      return parsedUrl.toString();
    } catch (err) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}_t=${Date.now()}`;
    }
  }, []);

  const retryCurrentVideo = useCallback(() => {
    const retryUrl = buildRetryUrl(currentVideoUrl);

    if (!retryUrl) {
      return;
    }

    setVideoLoadError(null);
    setIsVideoLoading(true);
    setCurrentVideoUrl(retryUrl);
  }, [buildRetryUrl, currentVideoUrl]);

  const handleVideoReady = useCallback(() => {
    setVideoLoadError(null);
    setIsVideoLoading(false);

    if (videoRef.current) {
      videoRef.current.muted = !isAudioUnlocked;
    }
  }, [isAudioUnlocked]);

  const handleVideoWaiting = useCallback(() => {
    setIsVideoLoading(true);
  }, []);

  const handleVideoLoadError = useCallback((event) => {
    console.error('视频加载错误:', event);
    setIsVideoLoading(false);
    setVideoLoadError('视频加载失败，请检查网络连接后重试');
  }, []);

  // 当出现新一轮分支时，清空上一次的选中状态，避免默认选中旧ID
  useEffect(() => {
    setSelectedBranchId(null);
  }, [branches]);

  // 当历史长度增加（进入新一集）时，重置当前临时选择，确保最新一集初始无高亮
  useEffect(() => {
    setSelectedBranchId(null);
  }, [storyHistory.length]);

  // 根据预览/当前模式切换展示数据（纯前端，不请求）
  // VF-001 修复：统一切换逻辑，确保手动切换/预览切换时也更新场景图和加载状态
  useEffect(() => {
    if (isPreviewing) {
      const idx = Math.max(0, Math.min(previewStepIndex, storyHistory.length - 1));
      const step = storyHistory[idx];
      if (!step) return;
      
      // 设置加载状态，显示过渡层
      setIsVideoLoading(true);
      // 清除视频错误状态
      setVideoLoadError(null);
      
      const videoUrl = step.video_url || null;
      setCurrentVideoUrl(videoUrl || null);
      // 更新场景图URL
      setCurrentSceneImageUrl(step.scene_image_url || null);
      // 预览模式下，分支仍然按"视频索引>=1才显示分支"的规则
      if (idx >= 1) {
        setBranches(step.branches || []);
      } else {
        setBranches([]);
      }
      setVideoPrompt(step.video_prompt || step.prompt || null);
    } else {
      // 非预览模式：根据当前播放索引显示对应的视频和分支
      if (storyHistory.length > 0 && currentPlayingIndex < storyHistory.length) {
        const step = storyHistory[currentPlayingIndex];
        if (step) {
          // 设置加载状态，显示过渡层
          setIsVideoLoading(true);
          // 清除视频错误状态
          setVideoLoadError(null);
          
          const videoUrl = step.video_url || null;
          setCurrentVideoUrl(videoUrl || null);
          // 更新场景图URL
          setCurrentSceneImageUrl(step.scene_image_url || null);
          // 视频N对应第N+1集的分支
          setBranches(step.branches || []);
          setVideoPrompt(step.video_prompt || step.prompt || null);
        }
      }
    }
  }, [isPreviewing, previewStepIndex, storyHistory, currentPlayingIndex]);

  /**
   * 获取故事数据（从 StoryLibrary 加载）
   */
  const fetchStory = useCallback(async (libId) => {
    if (isRequestingRef.current) {
      console.log('🔄 请求正在进行中，跳过重复请求');
      return;
    }

    if (!libId) {
      setError('缺少故事ID');
      setLoading(false);
      return;
    }

    console.log('🔄 获取故事数据:', libId);
    isRequestingRef.current = true;

    try {
      const response = await storyAPI.getStoryLibraryItem(libId);

      if (response && response.data && response.data.success) {
        const data = response.data.data;
        console.log('✅ 获取故事成功:', data);
        console.log('📊 故事状态:', data.status, '是否为 draft:', data.status === 'draft');

        setStoryData(data);

        // 更新历史（直接使用 history）
        if (data.history) {
          setStoryHistory(data.history);
          
            // 进入页面时，从头开始播放（视频0 = 故事背景）
          if (data.history.length > 0) {
            setCurrentPlayingIndex(0);
            const bgStep = data.history[0];
            
            // 设置第一个视频（故事背景）
            if (bgStep.video_url) {
              setCurrentVideoUrl(bgStep.video_url);
            }
            if (bgStep.scene_image_url) {
              setCurrentSceneImageUrl(bgStep.scene_image_url);
            }
            if (bgStep.video_prompt || bgStep.prompt) {
              setVideoPrompt(bgStep.video_prompt || bgStep.prompt);
            }
            
            // 视频N对应第N+1集分支
            setBranches(bgStep.branches || []);
            setCurrentRound(bgStep);
          }
        }

        // 背景音乐：优先使用 background_music.url，其次 background_music_url
        const musicUrl = (data.background_music && data.background_music.url) 
          || data.background_music_url 
          || null;
        if (musicUrl) {
          setBgmUrl(musicUrl);
        } else {
          setBgmUrl(null);
        }

        setLoading(false);
      } else {
        throw new Error('响应格式错误');
      }
    } catch (err) {
      console.error('❌ 获取故事失败:', err);
      const errorDetail = err.response?.data?.detail || err.message;
      const isNotFound = err.response?.status === 404 || errorDetail?.includes('不存在') || errorDetail?.includes('not found');
      
      if (isNotFound) {
        setError('故事不存在。请返回首页重新选择故事。');
      } else {
        setError('获取故事信息失败: ' + errorDetail);
      }
      setLoading(false);
    } finally {
      isRequestingRef.current = false;
    }
  }, []);

  // 当 bgmUrl 或 isMusicOn 变化时，确保音频元素加载并尝试播放
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (bgmUrl) {
      if (el.src !== bgmUrl) {
        el.src = bgmUrl;
        el.load();
      }
      if (isMusicOn) {
        el.loop = true;
        el.play().catch(() => {});
      }
    } else {
      el.pause();
      el.removeAttribute('src');
    }
  }, [bgmUrl, isMusicOn]);

  // 页面加载时获取故事数据
  useEffect(() => {
    if (currentLibraryId) {
      fetchStory(currentLibraryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLibraryId]);

  /**
   * 继续故事（选择分支）
   */
  const handleContinueStory = async (branchId) => {
    if (isGenerating || !branchId) {
      console.log('⚠️ 正在生成中或分支ID无效');
      return;
    }

    console.log('📖 继续故事，选择分支:', branchId);
    setSelectedBranchId(branchId);
    setIsGenerating(true);
    setGenerationError(null);
    setStatus('generating');
    setSuccessMessage(null); // 清除之前的完成提示

    // 立即更新UI：标记最新一步的选中分支
    if (storyHistory.length > 0) {
      const updatedHistory = [...storyHistory];
      const latestStep = updatedHistory[updatedHistory.length - 1];
      const selectedBranch = (latestStep.branches || []).find(b => 
        String(b.id || b.branch_id || '') === String(branchId)
      );
      if (selectedBranch) {
        latestStep.selected_branch = selectedBranch;
        setStoryHistory(updatedHistory);
      }
    }

    // 确保视频循环播放
    if (videoRef.current) {
      videoRef.current.loop = true;
      if (videoRef.current.paused) {
        videoRef.current.play().catch(console.error);
      }
    }

    try {
      // 发起继续故事请求
      const response = await storyAPI.continueStory(currentLibraryId, branchId);

      if (response && response.data && response.data.success) {
        const result = response.data;
        console.log('✅ 继续故事成功，步骤:', result.step);

        // 检查是否创建了新故事（从 published 开始新交互）
        if (result.is_new_story && result.library_id) {
          console.log('📝 创建了新故事，library_id:', result.library_id);
          // 更新当前 library_id
          setCurrentLibraryId(result.library_id);
          // 更新 URL（使用 replace 避免浏览器历史记录混乱）
          navigate(`/story/interact/${result.library_id}`, { replace: true });
        }

        setIsGenerating(false);
        setStatus('ready');
        // 清空上一集的临时选择，避免新一集出现已选高亮
        setSelectedBranchId(null);

        // 更新当前视频和分支
        const videoUrl = result.video_url || null;
        setCurrentVideoUrl(videoUrl);
        if (result.scene_image_url) {
          setCurrentSceneImageUrl(result.scene_image_url);
        }
        if (result.video_prompt) {
          setVideoPrompt(result.video_prompt);
        }
        setBranches(result.branches || []);
        
        // 刷新故事数据以更新历史
        const targetLibraryId = result.library_id || currentLibraryId;
        await fetchStory(targetLibraryId);
      } else {
        throw new Error('响应格式错误');
      }
    } catch (err) {
      console.error('❌ 继续故事失败:', err);
      setIsGenerating(false);
      setStatus('error');
      setGenerationError('继续故事失败: ' + (err.response?.data?.detail || err.message));
      setSelectedBranchId(null);
    }
  };

  /**
   * 视频播放事件处理
   */
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    setVideoEnded(false);
    // 用户与视频交互后，尝试启动背景音乐（规避浏览器自动播放限制）
    const el = audioRef.current;
    if (el && isMusicOn && el.src && el.paused) {
      el.loop = true;
      el.play().catch(() => {});
    }
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
    setVideoEnded(true);
    
    // 自动播放下一个视频
    if (storyHistory.length > 0 && currentPlayingIndex < storyHistory.length - 1) {
      const nextIndex = currentPlayingIndex + 1;
      
      // 切换路径统一走索引变化，避免自动切换与手动切换状态流分叉。
      setVideoLoadError(null);
      setIsVideoLoading(true);
      setCurrentPlayingIndex(nextIndex);
      
      // 自动播放下一个视频
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.muted = !isAudioUnlocked;
          videoRef.current.play().catch(err => {
            console.log('自动播放失败:', err);
          });
        }
      }, 100);
    } else if (storyHistory.length > 0 && currentPlayingIndex === storyHistory.length - 1) {
      // 已到最后一个视频：保持展示最后一集的分支，等待用户选择继续生成
      const lastStep = storyHistory[currentPlayingIndex];
      if (currentPlayingIndex >= 1) {
        setBranches(lastStep?.branches || []);
      } else {
        setBranches([]);
      }
      setCurrentRound(lastStep || null);
    }
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  /**
   * 重试生成（保留用于未来扩展）
   */
  // eslint-disable-next-line no-unused-vars
  const handleRetry = () => {
    setGenerationError(null);
    setStatus('ready');
    // 如果用户已选择分支，重新发起请求
    if (selectedBranchId) {
      handleContinueStory(selectedBranchId);
    }
  };

  /**
   * 返回首页
   */
  const handleGoHome = () => {
    navigate('/story');
  };

  // 切换背景音乐
  const toggleMusic = () => {
    const next = !isMusicOn;
    setIsMusicOn(next);
    const el = audioRef.current;
    if (!el) return;
    if (next) {
      el.loop = true;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  /**
   * 删除会话（保留用于未来扩展）
   */
  // eslint-disable-next-line no-unused-vars


  // 响应式优化
  useEffect(() => {
    const deviceType = getDeviceType();
    if (deviceType === 'mobile') {
      const branchButtons = document.querySelectorAll('.branch-button');
      branchButtons.forEach(button => {
        addTouchOptimization(button);
      });
    }
  }, [branches]);

  // 生成过程中确保视频循环播放
  useEffect(() => {
    if (isGenerating && videoRef.current) {
      videoRef.current.loop = true;
      if (videoRef.current.paused) {
        videoRef.current.play().catch(console.error);
      }
    }
  }, [isGenerating]);

  // 聊天窗口行为：内容更新后自动滚动到最底部
  useEffect(() => {
    if (rightPaneRef.current) {
      rightPaneRef.current.scrollTop = rightPaneRef.current.scrollHeight;
    }
  }, [storyHistory.length, isGenerating, successMessage, previewStepIndex]);

  // 在首次用户点击页面时尝试播放一次背景音乐（若存在且开启）
  useEffect(() => {
    const handler = () => {
      const el = audioRef.current;
      if (el && isMusicOn && el.src && el.paused) {
        el.loop = true;
        el.play().catch(() => {});
      }
      window.removeEventListener('click', handler, { capture: true });
    };
    window.addEventListener('click', handler, { capture: true });
    return () => window.removeEventListener('click', handler, { capture: true });
  }, [isMusicOn]);

  useEffect(() => {
    if (!currentVideoUrl || !videoRef.current) {
      return undefined;
    }

    const playbackTimer = window.setTimeout(() => {
      const currentVideo = videoRef.current;

      if (!currentVideo) {
        return;
      }

      currentVideo.muted = !isAudioUnlocked;
      currentVideo.play().catch(() => {});
    }, 0);

    return () => window.clearTimeout(playbackTimer);
  }, [currentVideoUrl, isAudioUnlocked]);

  // Phase 3: 视频预加载机制 - 预加载下一个视频
  useEffect(() => {
    if (storyHistory.length > currentPlayingIndex + 1) {
      const nextStep = storyHistory[currentPlayingIndex + 1];
      if (nextStep?.video_url && nextStep.video_url !== preloadVideoRef.current?.src) {
        // 清理之前的预加载元素
        if (preloadVideoRef.current) {
          try {
            document.body.removeChild(preloadVideoRef.current);
          } catch (e) {
            // 忽略已移除的情况
          }
        }
        
        // 创建新的预加载元素
        const preloadVideo = document.createElement('video');
        preloadVideo.src = nextStep.video_url;
        preloadVideo.preload = 'auto';
        preloadVideo.muted = true;
        preloadVideo.playsInline = true;
        preloadVideo.style.display = 'none';
        document.body.appendChild(preloadVideo);
        preloadVideo.load();
        preloadVideoRef.current = preloadVideo;
      }
    }
    
    return () => {
      if (preloadVideoRef.current) {
        try {
          document.body.removeChild(preloadVideoRef.current);
          preloadVideoRef.current = null;
        } catch (e) {
          // 忽略已移除的情况
        }
      }
    };
  }, [currentPlayingIndex, storyHistory]);

  // Phase 4: 音频解锁机制 - 用户首次交互后取消静音
  useEffect(() => {
    const unlockAudio = () => {
      if (!isAudioUnlocked) {
        setIsAudioUnlocked(true);
        // 取消视频静音
        if (videoRef.current) {
          videoRef.current.muted = false;
        }
      }
    };

    // 监听用户交互事件
    window.addEventListener('click', unlockAudio, { once: true });
    window.addEventListener('touchstart', unlockAudio, { once: true });
    
    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, [isAudioUnlocked]);

  // 加载状态
  if (loading) {
    return (
      <div className="App">
        <div className="main-layout">
          <nav className="sidebar-nav">
            <div className="nav-logo">
              <span className="logo-icon">🤖</span>
            </div>
            <div className="nav-menu">
              <a href="/story" className="nav-item" title="故事首页">
                <span className="nav-icon">📖</span>
              </a>
            </div>
          </nav>
          <div className="content-area">
            <div className="container">
              <div className="loading">
                <div className="loading-spinner"></div>
                <h2>正在加载故事会话...</h2>
                <p>请稍候</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error && !storyData) {
    return (
      <div className="App">
        <div className="main-layout">
          <nav className="sidebar-nav">
            <div className="nav-logo">
              <span className="logo-icon">🤖</span>
            </div>
            <div className="nav-menu">
              <a href="/story" className="nav-item" title="故事首页">
                <span className="nav-icon">📖</span>
              </a>
            </div>
          </nav>
          <div className="content-area">
            <div className="container">
              <div className="error">
                <h2>加载失败</h2>
                <p style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>{error}</p>
                <button
                  onClick={() => fetchStory(currentLibraryId)}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#007bff',
                    color: 'white',
                    cursor: 'pointer',
                    marginRight: '10px'
                  }}
                >
                  🔄 重试
                </button>
                <button
                  onClick={handleGoHome}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  🏠 返回首页
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 主页面渲染
  return (
    <div className="App">
      <div className="main-layout">
        {/* 左侧导航栏 */}
        <nav className="sidebar-nav">
          <div className="nav-logo">
            <span className="logo-icon">🤖</span>
          </div>
          <div className="nav-menu">
            <a href="/story" className="nav-item" title="故事首页">
              <span className="nav-icon">📖</span>
            </a>
          </div>
        </nav>

        <div className="content-area">
          <div className="container">
            {/* 顶部头部与额外提示移除，只保留左右两区 */}

            {/* 去除顶部生成中与错误提示，界面保持简洁 */}

            {/* 生成中提示移除，不占据页面空间 */}

            {/* 主内容区：左右布局 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              marginBottom: '20px'
            }}>
              {/* 左侧：视频播放区 */}
              <div
                onMouseEnter={() => setShowVideoNav(true)}
                onMouseLeave={() => setShowVideoNav(false)}
                style={{
                  background: '#000',
                  borderRadius: isMobileDevice ? 0 : '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  minHeight: isMobileDevice ? '100dvh' : '100vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {currentVideoUrl ? (
                  <>
                    {/* 视频过渡层 - 加载时显示场景图 */}
                    {isVideoLoading && currentSceneImageUrl && (
                      <img
                        src={currentSceneImageUrl}
                        alt="视频加载中"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: isMobileDevice ? '100dvh' : '100vh',
                          maxHeight: isMobileDevice ? '100dvh' : '100vh',
                          objectFit: isMobileDevice ? 'cover' : 'contain',
                          objectPosition: 'center center',
                          zIndex: 5,
                          pointerEvents: 'none',
                          opacity: 1,
                          transition: 'opacity 0.3s ease'
                        }}
                      />
                    )}
                    <video
                      ref={videoRef}
                      src={currentVideoUrl}
                      poster={currentSceneImageUrl}
                      muted={!isAudioUnlocked}
                      playsInline
                      controls
                      autoPlay
                      loop={isGenerating}
                      preload="auto"
                      style={{
                        width: '100%',
                        height: isMobileDevice ? '100dvh' : '100vh',
                        maxHeight: isMobileDevice ? '100dvh' : '100vh',
                        objectFit: isMobileDevice ? 'cover' : 'contain',
                        objectPosition: 'center center',
                        opacity: isVideoLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease'
                      }}
                      onPlay={handleVideoPlay}
                      onPause={handleVideoPause}
                      onEnded={handleVideoEnded}
                      onLoadedData={handleVideoReady}
                      onWaiting={handleVideoWaiting}
                      onCanPlay={handleVideoReady}
                      onError={handleVideoLoadError}
                    >
                      您的浏览器不支持视频播放
                    </video>
                  </>
                ) : currentSceneImageUrl ? (
                  <img
                    src={currentSceneImageUrl}
                    alt="场景图片"
                    style={{
                      width: '100%',
                      height: isMobileDevice ? '100dvh' : '100vh',
                      maxHeight: isMobileDevice ? '100dvh' : '100vh',
                      objectFit: isMobileDevice ? 'cover' : 'contain',
                      objectPosition: 'center center'
                    }}
                  />
                ) : (
                  <div style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>
                    <p>暂无视频</p>
                  </div>
                )}

                {/* 背景音乐音频元素（隐藏） */}
                <audio 
                  ref={audioRef} 
                  style={{ display: 'none' }} 
                  preload="auto"
                  src={bgmUrl || undefined}
                  onCanPlay={() => {
                    try {
                      if (isMusicOn && audioRef.current && audioRef.current.paused) {
                        audioRef.current.loop = true;
                        audioRef.current.play().catch(() => {});
                      }
                    } catch {}
                  }}
                  onError={(e) => {
                    // eslint-disable-next-line no-console
                    console.error('BGM 加载失败:', audioRef.current?.src, e);
                  }}
                  onPlay={() => {
                    // eslint-disable-next-line no-console
                    console.log('BGM 已开始播放:', audioRef.current?.src);
                  }}
                />

                {/* 右上角音乐开关（小按钮） */}
                <button
                  onClick={toggleMusic}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 20,
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    padding: '6px 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  {isMusicOn ? '🔊 BGM' : '🔇 BGM'}
                </button>

                {/* VF-002 修复：视频加载错误提示与重试入口 */}
                {videoLoadError && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    background: 'rgba(0,0,0,0.9)',
                    color: '#fff',
                    padding: '24px 32px',
                    borderRadius: 12,
                    textAlign: 'center',
                    border: '1px solid #dc3545',
                    maxWidth: '80%'
                  }}>
                    <div style={{ fontSize: 24, marginBottom: 12 }}>⚠️</div>
                    <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>视频加载失败</div>
                    <div style={{ fontSize: 14, marginBottom: 16, color: '#aaa' }}>{videoLoadError}</div>
                    <button
                      onClick={retryCurrentVideo}
                      style={{
                        padding: '10px 24px',
                        fontSize: 14,
                        borderRadius: 8,
                        border: 'none',
                        backgroundColor: '#007bff',
                        color: 'white',
                        cursor: 'pointer',
                        marginRight: 8
                      }}
                    >
                      🔄 重试
                    </button>
                    <button
                      onClick={() => setVideoLoadError(null)}
                      style={{
                        padding: '10px 24px',
                        fontSize: 14,
                        borderRadius: 8,
                        border: '1px solid #666',
                        backgroundColor: 'transparent',
                        color: '#aaa',
                        cursor: 'pointer'
                      }}
                    >
                      关闭
                    </button>
                  </div>
                )}

                {/* 底部轮次与左右导航 */}
                <div style={{
                  position: 'absolute',
                  bottom: '56px',
                  left: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 10px',
                  color: '#eaeaea',
                  opacity: showVideoNav ? 1 : 0,
                  transition: 'opacity 0.2s ease'
                }}>
                  <button
                    onClick={() => {
                      const targetIdx = isPreviewing 
                        ? (previewStepIndex !== null ? previewStepIndex - 1 : currentPlayingIndex - 1)
                        : currentPlayingIndex - 1;
                      if (targetIdx >= 0) {
                        if (isPreviewing) {
                          setPreviewStepIndex(targetIdx);
                        } else {
                          setCurrentPlayingIndex(targetIdx);
                        }
                      }
                    }}
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
                  >
                    ← 上一集
                  </button>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>
                    {(() => {
                      const idx = isPreviewing 
                        ? (previewStepIndex !== null ? previewStepIndex : currentPlayingIndex)
                        : currentPlayingIndex;
                      // 索引0 = 第一集，索引1 = 第二集，索引2 = 第三集...
                      return `第 ${idx + 1} 集`;
                    })()}
                  </div>
                  <button
                    onClick={() => {
                      const currentIdx = isPreviewing 
                        ? (previewStepIndex !== null ? previewStepIndex : currentPlayingIndex)
                        : currentPlayingIndex;
                      const nextIdx = currentIdx + 1;
                      if (nextIdx < storyHistory.length) {
                        if (isPreviewing) {
                          setPreviewStepIndex(nextIdx);
                        } else {
                          setCurrentPlayingIndex(nextIdx);
                        }
                      }
                    }}
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
                  >
                    下一集 →
                  </button>
                </div>
              </div>

              {/* 右侧：对话流式交互区 */}
              <div
                ref={rightPaneRef}
                style={{
                background: '#0b0b0b',
                borderRadius: '8px',
                padding: '20px',
                  height: '100vh',
                  maxHeight: '100vh',
                overflowY: 'auto',
                color: '#eaeaea',
                border: '1px solid #1f1f1f',
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
                }}
              >
                {/* 故事背景信息 */}
                {storyData && storyData.story && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#111',
                    borderRadius: '6px',
                    border: '1px solid #1f1f1f',
                    color: '#dcdcdc'
                  }}>
                    <h4 style={{ fontSize: '14px', marginBottom: '10px', color: '#a0a0a0' }}>故事背景</h4>
                    <p style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      {storyData.story}
                    </p>
                  </div>
                )}

                {/* 分轮对话流：展示每轮的分支选项 */}
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {storyHistory.map((step, idx) => {
                    // 核心逻辑：
                    // - storyHistory[idx] 是第 idx+1 个视频
                    // - storyHistory[idx] 的 branches 用于生成第 idx+2 个视频
                    // - storyHistory[idx+1] 的 selected_branch 记录选择了哪个分支来生成第 idx+2 个视频
                    // - 所以当播放第 idx+2 个视频时，应该在 storyHistory[idx] 的 branches 中勾选 storyHistory[idx+1].selected_branch
                    
                    const isCurrentPlaying = !isPreviewing && currentPlayingIndex === idx;
                    const isLatest = idx === storyHistory.length - 1;
                    const options = step.branches || [];
                    
                    // 标题：索引0显示"第一集后的候选分支"，索引1显示"第二集后的候选分支"，以此类推
                    const displayTitle = `第 ${idx + 1} 集后的候选分支`;
                    
                    // 是否可交互：只有最新集且正在播放时才能选择分支
                    const canInteract = isLatest && !isPreviewing && !isGenerating && isCurrentPlaying;
                    
                    // 关键修复：selected_branch 保存在下一个 step 中
                    // storyHistory[idx+1].selected_branch 记录选择了哪个分支来生成 storyHistory[idx+1]
                    // 这个分支应该在 storyHistory[idx].branches 中
                    const nextStep = idx + 1 < storyHistory.length ? storyHistory[idx + 1] : null;
                    const selectedBranch = nextStep ? (nextStep.selected_branch || nextStep.select_branch) : null;
                    const selectedBranchId_persisted = selectedBranch 
                      ? (selectedBranch.id || selectedBranch.branch_id) 
                      : null;

                    return (
                      <div 
                        key={idx} 
                        style={{ 
                          background: isCurrentPlaying ? '#1a1a2e' : '#0f0f0f', 
                          border: '1px solid', 
                          borderColor: isCurrentPlaying ? '#4c6fff' : '#1f1f1f', 
                          borderRadius: 8, 
                          padding: 12,
                          opacity: idx > currentPlayingIndex ? 0.5 : 1
                        }}
                      >
                        <div style={{ fontSize: 13, color: '#9aa0a6', marginBottom: 8 }}>
                          {displayTitle} {isCurrentPlaying && !isPreviewing ? '(正在播放)' : ''}
                        </div>
                        
                        {options.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {options.map((branch, bIndex) => {
                              const label = branch.text || branch.branch_summary || branch.scene || `选项${bIndex + 1}`;
                              const bid = branch.id || branch.branch_id || String(bIndex);

                              // 勾选逻辑（修复索引对应关系）：
                              // - 如果 currentPlayingIndex > idx：已经播放过了，必须勾选对应的分支（显示选择路径）
                              //   selected_branch 保存在下一个 step（storyHistory[idx+1]）中
                              // - 如果 currentPlayingIndex === idx：当前播放，未勾选（等待选择）
                              // - 如果正在生成：临时勾选用户刚选择的分支
                              let isChosen = false;
                              
                              // 关键：只有已经播放过的视频（currentPlayingIndex > idx）才应该勾选
                              // 当前正在播放的视频（currentPlayingIndex === idx）不应该勾选
                              if (currentPlayingIndex > idx) {
                                // 已经播放到后面的视频了，说明这个分支已经做出了选择
                                // selected_branch 保存在下一个 step 中
                                if (selectedBranchId_persisted) {
                                  // 有保存的选择，勾选对应的分支
                                  isChosen = String(selectedBranchId_persisted) === String(bid);
                                }
                              } else if (currentPlayingIndex === idx && isLatest && isGenerating && selectedBranchId) {
                                // 当前正在播放，且正在生成下一集，临时勾选用户刚选择的分支
                                isChosen = String(selectedBranchId) === String(bid);
                              }
                              // currentPlayingIndex === idx 且未生成时，不勾选（等待选择）

                              // 是否禁用：历史分支只读，只有最新集可交互
                              const isDisabled = !canInteract || isGenerating;
                              
                              // 是否显示删除按钮：只有最新集且未选择的分支可以删除，且至少保留一个分支
                              const canDelete = isLatest && !isChosen && options.length > 1 && !isGenerating;
                              
                              return (
                                <div
                                  key={bid}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                  }}
                                >
                                  <label
                                    style={{
                                      flex: 1,
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      padding: '12px',
                                      borderRadius: 8,
                                      border: '2px solid',
                                      borderColor: isChosen ? '#4c6fff' : '#2a2a2a',
                                      background: isChosen ? '#1a233a' : '#141414',
                                      color: '#eaeaea',
                                      cursor: isDisabled ? 'default' : 'pointer',
                                      transition: 'all 0.2s',
                                      opacity: isDisabled && !isChosen ? 0.6 : 1
                                    }}
                                    onClick={(e) => {
                                      if (canInteract && !isGenerating && !isChosen) {
                                        e.preventDefault();
                                        setSelectedBranchId(bid);
                                        handleContinueStory(bid);
                                      }
                                    }}
                                  >
                                    <span
                                      aria-hidden
                                      style={{
                                        width: 18,
                                        height: 18,
                                        minWidth: 18,
                                        borderRadius: '50%',
                                        border: `2px solid ${isChosen ? '#4c6fff' : '#666'}`,
                                        background: isChosen ? '#4c6fff' : 'transparent',
                                        color: '#fff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: 12,
                                        marginTop: 2,
                                        fontSize: 12,
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {isChosen ? '✓' : ''}
                                    </span>
                                    <span style={{ flex: 1, lineHeight: '1.5' }}>{label}</span>
                                  </label>
                                  {canDelete && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!window.confirm(`确定要删除这个分支吗？\n"${label}"`)) {
                                          return;
                                        }
                                        try {
                                          const response = await storyAPI.deleteBranch(currentLibraryId, bid);
                                          if (response?.data?.success) {
                                            // 刷新故事数据以更新分支列表
                                            await fetchStory(currentLibraryId);
                                          } else {
                                            alert('删除失败: ' + (response?.data?.detail || '未知错误'));
                                          }
                                        } catch (err) {
                                          console.error('删除分支失败:', err);
                                          alert('删除失败: ' + (err.response?.data?.detail || err.message));
                                        }
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        background: '#dc3545',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 'bold',
                                        transition: 'all 0.2s',
                                        opacity: 0.8
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = 1;
                                        e.currentTarget.style.background = '#c82333';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = 0.8;
                                        e.currentTarget.style.background = '#dc3545';
                                      }}
                                      title="删除此分支"
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ color: '#9a9a9a', fontSize: 13 }}>
                            {idx === 0 ? '故事背景无分支选项' : '本集无分支（片头或结局）'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* 发布按钮 - 仅当故事是 draft 状态时显示，放在分支选择下方 */}
                  {storyData && storyData.status === 'draft' && !isGenerating && (
                    <div style={{
                      marginTop: '24px',
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #4c6fff 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      border: '2px solid #6b8cff',
                      boxShadow: '0 8px 24px rgba(76, 111, 255, 0.4)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      width: '100%',
                      maxWidth: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(76, 111, 255, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(76, 111, 255, 0.4)';
                    }}
                    onClick={async () => {
                      if (!window.confirm('确定要发布这个故事到广场吗？发布后其他用户可以看到并继续创作。')) {
                        return;
                      }
                      try {
                        const response = await storyAPI.updateStoryStatus(currentLibraryId, 'published');
                        if (response?.data?.success) {
                          alert('✓ 故事已成功发布到广场！');
                          // 刷新故事数据以更新状态
                          await fetchStory(currentLibraryId);
                          // 跳转到首页查看发布的故事
                          navigate('/story');
                        } else {
                          throw new Error('发布失败');
                        }
                      } catch (err) {
                        console.error('发布失败:', err);
                        alert('发布失败: ' + (err.response?.data?.detail || err.message));
                      }
                    }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '15px',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <span style={{ fontSize: '20px' }}>🚀</span>
                        <span>分享给大家</span>
                      </div>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transition: 'left 0.5s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.left = '100%';
                      }}
                      />
                    </div>
                  )}
                  
                  {/* 生成状态消息 */}
                  {isGenerating && storyHistory.length > 0 && (
                    <div style={{ 
                      background: '#101010', 
                      border: '1px dashed #4c6fff', 
                      borderRadius: 8, 
                      padding: 12,
                      animation: 'pulse 2s ease-in-out infinite',
                      boxShadow: '0 0 10px rgba(76, 111, 255, 0.3)'
                    }}>
                      {(() => {
                        // 当前正在生成的是第 storyHistory.length + 1 个视频
                        // 索引 storyHistory.length 对应第 storyHistory.length + 1 集
                        const nextVideoIndex = storyHistory.length; // 即将生成的新视频索引
                        const nextEpisodeNum = nextVideoIndex + 1; // 新视频对应的集数（索引0=第1集，索引1=第2集...）
                        const displayEpisode = `第 ${nextEpisodeNum} 集`;
                        
                        const chosenLabel = (() => {
                          const latest = storyHistory[storyHistory.length - 1];
                          const opt = (latest?.branches || []).find(b => String(b.id || b.branch_id || '') === String(selectedBranchId));
                          return opt ? (opt.text || opt.branch_summary || opt.scene || '所选分支') : '所选分支';
                        })();
                        return (
                          <div style={{ color: '#cfcfcf', fontSize: 13, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              display: 'inline-block',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#4c6fff',
                              animation: 'blink 1.5s ease-in-out infinite'
                            }}></span>
                            正在为你生成「{chosenLabel}」的{displayEpisode}剧情...
                          </div>
                        );
                      })()}
                      <style>{`
                        @keyframes pulse {
                          0%, 100% {
                            opacity: 1;
                            border-color: #4c6fff;
                          }
                          50% {
                            opacity: 0.7;
                            border-color: #6b8cff;
                          }
                        }
                        @keyframes blink {
                          0%, 100% {
                            opacity: 1;
                            transform: scale(1);
                          }
                          50% {
                            opacity: 0.3;
                            transform: scale(0.8);
                          }
                        }
                      `}</style>
                    </div>
                  )}
                  {/* 完成状态提示 */}
                  {!isGenerating && successMessage && (
                    <div style={{ background: '#0f2812', border: '1px solid #1f5f2a', borderRadius: 8, padding: 12, color: '#d5f5dc' }}>
                      {successMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* 移除底部历史列表区域 */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorySessionPage;


